/**
 * What pressing an agent-proposed action button does.
 *
 * Declared as data so the backend owns it and the frontend executes it, instead
 * of both hand-maintaining the same table. Before this, the API declared the 12
 * action keys and `ui/app-portal` independently mapped the same 12 keys to URLs
 * — with `key: string` on the wire, so a new backend action silently resolved to
 * `null` in the UI.
 *
 * `:name` placeholders in `path` and `query` are filled from the action's own
 * validated params.
 */
export type AgentEffect =
  /** Send the user somewhere in the app. */
  | {
      readonly kind: 'navigate';
      readonly path: string;
      readonly query?: Readonly<Record<string, string>>;
    }
  /**
   * Call an existing guarded API route. The request goes through the normal
   * JwtAuthGuard → AuthContextGuard → OrganizationTypesGuard → RolesGuard chain,
   * so proposing an action creates no new authorization surface.
   */
  | {
      readonly kind: 'mutate';
      readonly method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
      readonly path: string;
      readonly body?: Readonly<Record<string, unknown>>;
      readonly confirm: string;
    }
  /** A UI flow the backend cannot express — a prefilled or multi-step form. */
  | {
      readonly kind: 'handler';
      readonly handler: string;
      readonly prefill?: Readonly<Record<string, unknown>>;
    };

/** A `:name` placeholder. */
const PLACEHOLDER = /:([a-z_][a-z0-9_]*)/gi;

/**
 * Reads a placeholder's value, or null if it is unusable.
 *
 * Empty strings and non-strings count as absent: the model produced these
 * params, so a missing id must yield no link rather than a URL like `/parts/`.
 */
function lookup(params: Record<string, unknown>, name: string): string | null {
  const value = params[name];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Fills `:name` placeholders in a path. Returns null if any is unresolvable, so
 * a half-built path can never reach the router.
 */
export function interpolatePath(path: string, params: Record<string, unknown>): string | null {
  let resolvable = true;

  const filled = path.replace(PLACEHOLDER, (_match, name: string) => {
    const value = lookup(params, name);
    if (value === null) {
      resolvable = false;
      return '';
    }
    return encodeURIComponent(value);
  });

  return resolvable ? filled : null;
}

/**
 * Builds a query string from a template, dropping entries whose placeholder has
 * no usable value. Values are encoded by URLSearchParams — user-supplied text
 * must never land in a URL raw.
 */
export function buildQueryString(
  template: Readonly<Record<string, string>> | undefined,
  params: Record<string, unknown>,
): string {
  if (!template) return '';

  const search = new URLSearchParams();
  for (const [key, placeholder] of Object.entries(template)) {
    const match = PLACEHOLDER.exec(placeholder);
    PLACEHOLDER.lastIndex = 0;

    const value = match ? lookup(params, match[1]) : placeholder;
    if (value !== null && value !== '') search.set(key, value);
  }

  const suffix = search.toString();
  return suffix ? `?${suffix}` : '';
}

/**
 * Resolves a navigate effect to an in-app path, or null if it cannot be built.
 *
 * Always relative and always rooted at `/`: an action must not be able to
 * navigate off-origin, and these effects are authored server-side, so the
 * assertion is cheap insurance rather than input validation.
 */
export function resolveNavigateEffect(
  effect: Extract<AgentEffect, { kind: 'navigate' }>,
  params: Record<string, unknown>,
): string | null {
  const path = interpolatePath(effect.path, params);
  if (path === null) return null;
  if (!path.startsWith('/') || path.startsWith('//')) return null;

  return `${path}${buildQueryString(effect.query, params)}`;
}
